import { groupBy, startCase, uniqBy } from "lodash";
import { DateTime } from "luxon";
import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

import {
  IgCloseButton,
  IgPaper,
  IgSelect,
  IgTypography,
} from "@intelligems/ig-theme";
import { PluginPerformanceType } from "@intelligems/ig-types";
import { ensureNumber, ensureString } from "@intelligems/ig-utils";
import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";

import { useGraphValueFormatter } from "../../api/formatters/dashboard";
import NoDataOverlay from "../../components/NoDataOverlay/NoDataOverlay";
import {
  removePerformanceView,
  setPerformanceView,
} from "../../store/slices/appSettings";
import { PerformanceView } from "../../store/types/appSettings";
import theme from "../../theme";
import { lineGroupColors } from "../Dashboard/Graphs/Utils/graphHelpers";

import { titleFormatter } from "./utils";

interface PerformanceLineChartProps {
  data: PluginPerformanceType[];
  index: number;
  total: number;
  view: PerformanceView;
}

const PerformanceTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<ValueType, NameType>) => {
  if (active && payload && payload.length) {
    if (payload) {
      return (
        <IgPaper
          sx={{
            padding: "10px",
            backgroundColor: "rgba(97, 97, 97, 0.92)",
            border: "1px solid transparent",
            borderRadius: "5px",
          }}
        >
          <Grid container item direction={"column"}>
            <Grid item>
              <IgTypography sx={{ color: "#FFF", fontSize: "14px" }}>
                <b>Date:</b>{" "}
                {DateTime.fromSeconds(payload[0].payload.completed).toFormat(
                  "yyyy-MM-dd hh:mm"
                )}
              </IgTypography>
            </Grid>
            {payload.map((value) => {
              if (value.dataKey && value.value !== undefined) {
                return (
                  <Grid item key={value.dataKey} sx={{ marginTop: "10px" }}>
                    <IgTypography sx={{ color: "#FFF", fontSize: "14px" }}>
                      <b>
                        {ensureString(value.dataKey).includes("-blocked")
                          ? "Value without Intelligems"
                          : "Value with Intelligems"}
                        :
                      </b>{" "}
                      {ensureNumber(value.value as number).toFixed(2)}
                    </IgTypography>
                    <Grid item>
                      <IgTypography sx={{ color: "#FFF", fontSize: "14px" }}>
                        <b>
                          {ensureString(value.dataKey).includes("-blocked")
                            ? "Test without Intelligems"
                            : "Test with Intelligems"}
                          :
                        </b>{" "}
                        {payload[0].payload.id}
                      </IgTypography>
                    </Grid>
                  </Grid>
                );
              }
            })}
          </Grid>
        </IgPaper>
      );
    }
  }

  return null;
};

export const PerformanceLineChart = ({
  data,
  index,
  total,
  view,
}: PerformanceLineChartProps) => {
  const dispatch = useDispatch();
  const graphValueFormatter = useGraphValueFormatter();

  const chartData = useMemo(() => {
    const groupedByTime = groupBy(data, (_) => _.completed);
    const finalData: PluginPerformanceType[] = [];

    for (const [time, values] of Object.entries(groupedByTime)) {
      const reduced: PluginPerformanceType = values.reduce(
        (prev: PluginPerformanceType, curr: PluginPerformanceType) => {
          return {
            ...prev,
            ...curr,
            results: {
              ...prev.results,
              ...curr.results,
            },
          };
        },
        // @ts-ignore
        {}
      );
      finalData.push(reduced);
    }
    return finalData;
  }, [data]);

  const dropdownKeys = useMemo(() => {
    if (!data.length) {
      return [];
    }

    let keys = Object.keys(data[0].results || {});
    keys = keys.map((key) => key.replace("-blocked", ""));
    keys = uniqBy(keys, (_) => _);
    return keys;
  }, [data]);

  return (
    <IgPaper>
      {data.length ? (
        <Grid container direction={"row"} sx={{ width: "100%" }} rowSpacing={2}>
          <Grid
            container
            item
            direction={"row"}
            justifyContent={"space-between"}
            flexWrap={"nowrap"}
          >
            <Grid item>
              <IgSelect
                sx={{ backgroundColor: "white" }}
                id={`performance-view-select-${index}`}
                value={view.dataKey || "TBT"}
                onChange={(e: SelectChangeEvent<any>) => {
                  dispatch(
                    setPerformanceView({
                      index,
                      value: { dataKey: e.target.value },
                    })
                  );
                }}
              >
                {dropdownKeys.length ? (
                  dropdownKeys.map((item) => (
                    <MenuItem key={item} value={item}>
                      {titleFormatter(item, true)}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value={"TBT"}>First Paint</MenuItem>
                )}
              </IgSelect>
            </Grid>
            <Grid item justifyContent={"end"}>
              {total > 1 ? (
                <IgCloseButton
                  onClick={() => dispatch(removePerformanceView({ index }))}
                />
              ) : null}
            </Grid>
          </Grid>
          <Grid item sx={{ width: "100%" }}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={chartData}
                style={{ marginLeft: -24 }}
                margin={{
                  top: 5,
                  bottom: 5,
                }}
              >
                <CartesianGrid
                  style={{ stroke: theme.palette.disabled.light }}
                  vertical={false}
                />
                <XAxis
                  // allowDuplicatedCategory={false}
                  label={
                    <Label
                      style={{
                        fill: theme.palette.text.primary,
                        fontFamily: theme.typography.fontFamily,
                        fontSize: 14,
                      }}
                      value={"Date"}
                      position={"insideBottom"}
                      offset={-5}
                      textAnchor={"middle"}
                    />
                  }
                  padding={{ left: 30, right: 30 }}
                  axisLine={{ stroke: theme.palette.disabled.light }}
                  tickLine={false}
                  tickFormatter={(item) =>
                    DateTime.fromSeconds(item).toFormat("yyyy-MM-dd hh:mm")
                  }
                  dataKey={"completed"}
                />
                <YAxis
                  label={
                    <Label
                      style={{
                        fill: theme.palette.text.primary,
                        fontFamily: theme.typography.fontFamily,
                        fontSize: 14,
                        marginTop: "10px",
                        textAnchor: "middle",
                      }}
                      angle={270}
                      value={startCase(view.dataKey)}
                      position={"insideLeft"}
                      offset={10}
                      textAnchor={"middle"}
                    />
                  }
                  style={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: 14,
                  }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: any) =>
                    graphValueFormatter(true, value, {
                      dataTypes: ["numberNoDecimal"],
                    })!
                  }
                />
                <Tooltip content={<PerformanceTooltip />} />
                <Legend
                  verticalAlign="top"
                  height={36}
                  formatter={(item) =>
                    titleFormatter(item.replace("results.", ""), false).replace(
                      "Blocked",
                      "Without Intelligems"
                    )
                  }
                />
                <Line
                  type="linear"
                  dataKey={`results.${view.dataKey}-blocked`}
                  stroke={lineGroupColors[3]}
                  connectNulls={true}
                />
                <Line
                  type="linear"
                  dataKey={`results.${view.dataKey}`}
                  stroke={lineGroupColors[0]}
                  connectNulls={true}
                />
              </LineChart>
            </ResponsiveContainer>
          </Grid>
        </Grid>
      ) : (
        <NoDataOverlay
          message1={"No Data Yet!"}
          message2={"Try running a performance check."}
        />
      )}
    </IgPaper>
  );
};
